/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { MonitorAgent } from "@/api/monitor";
import { useMonitorAgent } from "@/hooks/useMonitorAgent";
import { Bar, StatusDot, fmtDuration, fmtPct, fmtRate } from "./kit";
import { AnimatedNumber } from "@/components/common/AnimatedNumber";

export function useAgentOnline(status?: { connected?: boolean; reachable?: boolean }): boolean {
  return !!(status?.connected || status?.reachable);
}

interface ServerCardProps {
  agent: MonitorAgent;
  selected: boolean;
  onSelect: () => void;
}

export const ServerCard: React.FC<ServerCardProps> = ({ agent, selected, onSelect }) => {
  const { status, hardwareStats, systemInfo, isLoadingStatus } = useMonitorAgent({
    agent,
    includeHardwareStats: true,
    includeSystemInfo: true,
  });
  const online = useAgentOnline(status);
  const state: "up" | "down" | "idle" = !agent.enabled ? "idle" : isLoadingStatus ? "idle" : online ? "up" : "down";
  const stateText = !agent.enabled ? "disabled" : isLoadingStatus ? "checking" : online ? "online" : "offline";
  const hw = hardwareStats;
  const live = status?.liveData;

  // Show just host[:port] -- drop scheme and any /events… path.
  const host = (agent.url || "").replace(/^https?:\/\//, "").replace(/\/.*$/, "");

  const uptime = systemInfo?.uptime
    ? `up ${fmtDuration(systemInfo.uptime)}`
    : status?.health?.uptime_seconds
      ? `agent ${fmtDuration(status.health.uptime_seconds)}`
      : "";
  const ready = agent.enabled && hw && online;

  return (
    <button
      className={`sv-card sv-card--${state}${selected ? " sv-card--sel" : ""}`}
      onClick={onSelect}
      aria-pressed={selected}
      title={stateText}
    >
      <span className="sv-card-accent" aria-hidden="true" />
      <div className="sv-card-top">
        <StatusDot state={state} pulse={isLoadingStatus} />
        <span className="sv-card-name">{agent.name}</span>
        <span className="sv-card-up">{ready ? uptime : stateText}</span>
      </div>
      <span className="sv-card-host">{host || " "}</span>

      <div className="sv-card-rows">
        <div className="sv-card-row">
          <span>CPU</span>
          {ready ? <Bar pct={hw.cpu.usage_percent} /> : <i className="sv-card-bar-off" />}
          <b>{ready ? <AnimatedNumber value={hw.cpu.usage_percent} mountFrom={null} format={(n) => fmtPct(n)} /> : "—"}</b>
        </div>
        <div className="sv-card-row">
          <span>RAM</span>
          {ready ? <Bar pct={hw.memory.used_percent} warn={80} crit={92} /> : <i className="sv-card-bar-off" />}
          <b>{ready ? <AnimatedNumber value={hw.memory.used_percent} mountFrom={null} format={(n) => fmtPct(n)} /> : "—"}</b>
        </div>
      </div>

      <div className="sv-card-foot">
        {ready && live ? (
          <>
            <span>↓ {fmtRate(live.rx.bytespersecond)}</span>
            <span>↑ {fmtRate(live.tx.bytespersecond)}</span>
          </>
        ) : (
          <span>{agent.enabled ? (online ? "loading…" : "agent unreachable") : "monitoring disabled"}</span>
        )}
      </div>
    </button>
  );
};
